import { useRef, useLayoutEffect, useState } from "react";
import { EditableCombobox } from "@/components/EditableCombobox";
import { MedicineSearch } from "@/components/MedicineSearch";
import { computeMedTotal } from "@/lib/medUtils";
import { TABLE_COLS, SIGNATURE, DOCTOR_PICK, MED_ROW_CENTER0, MED_ROW_STEP, NOTES } from "@/lib/alignment";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fmtDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Renders the trade name and salt combination inside the medicine cell.
 * The whole block is vertically centred; long text wraps within the cell,
 * and if the salt still overflows its font size is reduced (name unchanged).
 */
function MedNameCell({ name, salt, nameFontPt }) {
  const boxRef = useRef(null);
  const contentRef = useRef(null);
  const saltRef = useRef(null);
  const maxSaltPt = Math.max(nameFontPt - 3, 6);
  const [saltPt, setSaltPt] = useState(maxSaltPt);

  useLayoutEffect(() => {
    const box = boxRef.current;
    const content = contentRef.current;
    const saltEl = saltRef.current;
    if (!box || !content) return;
    let pt = maxSaltPt;
    if (saltEl && salt) {
      saltEl.style.fontSize = `${pt}pt`;
      let guard = 0;
      while (pt > 4.5 && content.scrollHeight > box.clientHeight && guard < 40) {
        pt -= 0.5;
        saltEl.style.fontSize = `${pt}pt`;
        guard += 1;
      }
    }
    setSaltPt(pt);
  }, [name, salt, nameFontPt, maxSaltPt]);

  return (
    <div ref={boxRef} style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
      <div ref={contentRef}>
        <div style={{ fontSize: `${nameFontPt}pt`, fontWeight: 700, lineHeight: 1.05, wordBreak: "break-word", overflowWrap: "anywhere" }}>{name}</div>
        {salt ? (
          <div ref={saltRef} className="text-slate-500" style={{ fontSize: `${saltPt}pt`, lineHeight: 1.0, wordBreak: "break-word", overflowWrap: "anywhere" }}>{salt}</div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Renders up to 4 notes stacked vertically inside the notes area. Long text
 * wraps within the fixed width and is clipped to the area (never overflows).
 */
function NotesBlock({ value, onChange, options, addOption, deleteOption, editable, fontSizePt, style }) {
  const arr = String(value || "").split("\n").filter((s) => s.trim() !== "").slice(0, 4);
  if (editable) {
    const shown = Math.min(arr.length + 1, 4);
    const rows = Array.from({ length: shown }, (_, i) => arr[i] || "");
    const change = (i, v) => {
      const next = rows.slice();
      next[i] = v;
      onChange(next.filter((s) => s.trim() !== "").slice(0, 4).join("\n"));
    };
    return (
      <div style={style} className="font-paper" data-testid="pp-notes">
        {rows.map((val, i) => (
          <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "1mm", fontSize: `${fontSizePt}pt`, lineHeight: 1.15, marginBottom: "0.5mm" }}>
            <span style={{ visibility: val.trim() ? "visible" : "hidden" }}>•</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <EditableCombobox field="notes" value={val} onChange={(v) => change(i, v)}
                options={options} onAdd={addOption} onDelete={deleteOption}
                placeholder={i === 0 ? "Add note…" : "Add another…"} testid={`field-notes-${i}`} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ ...style, overflow: "hidden" }} className="font-paper" data-testid="pp-notes">
      {arr.map((note, i) => (
        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "1mm", fontSize: `${fontSizePt}pt`, lineHeight: 1.2, marginBottom: "0.5mm" }}>
          <span>•</span>
          <div style={{ flex: 1, minWidth: 0, wordBreak: "break-word", overflowWrap: "anywhere" }}>{note}</div>
        </div>
      ))}
    </div>
  );
}

/**
 * Absolutely-positioned layer used for PRE-PRINTED output. Every value is placed
 * at exact mm coordinates from the saved alignment config so it lands in the
 * correct blank areas of the physical clinic letterhead.
 */
export function PreprintLayer({ data, setData, options = {}, addOption, deleteOption, align, editable = false, showGuides = false, transparent = false, templateSrc = null, printTemplate = false, exportMode = false, medCatalog = [], onAddMedicine, doctorList = [], doctor = "", onDoctorChange, signature = "" }) {
  const f = align.fields;
  const t = align.table;
  const ox = align.offsetLeft || 0;
  const oy = align.offsetTop || 0;

  const opt = (k) => options[k] || [];
  const setField = (k, v) => setData && setData((d) => ({ ...d, [k]: v }));
  const setMed = (i, k, v) =>
    setData &&
    setData((d) => ({ ...d, medicines: d.medicines.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)) }));

  const applyMed = (i, med) =>
    setData &&
    setData((d) => ({
      ...d,
      medicines: d.medicines.map((m, idx) => {
        if (idx !== i) return m;
        const upd = { ...m, name: med.trade || "", salt: med.salt || "" };
        ["food", "morning", "afternoon", "night", "duration"].forEach((k) => {
          if (med[k] !== undefined && med[k] !== "") upd[k] = med[k];
        });
        return upd;
      }),
    }));

  const clearMed = (i) =>
    setData &&
    setData((d) => ({
      ...d,
      medicines: d.medicines.map((m, idx) =>
        idx === i ? { name: "", salt: "", food: "", morning: "", afternoon: "", night: "", duration: "", total_qty: "" } : m
      ),
    }));

  // cumulative left offset (mm) for each column
  const colLeft = {};
  let acc = t.left;
  TABLE_COLS.forEach((c) => {
    colLeft[c] = acc;
    acc += t.cols[c];
  });

  // html2canvas renders text ~2.4mm lower than the browser; compensate only for export capture
  const comp = exportMode ? 2.4 : 0;

  const pos = (cfg) => ({
    position: "absolute",
    top: `${oy + cfg.top - comp}mm`,
    left: `${ox + cfg.left}mm`,
    width: `${cfg.width}mm`,
    fontSize: `${cfg.fontSize}pt`,
  });

  const renderField = ({ fieldKey, value, onChange, optKey, isDate, inline }) => {
    const cfg = f[fieldKey];
    return (
      <div style={pos(cfg)} className="font-paper leading-tight" data-testid={`pp-${fieldKey}`}>
        {editable ? (
          isDate ? (
            <input type="date" value={value || ""} onChange={(e) => onChange(e.target.value)}
              className="combo-underline bg-transparent outline-none w-full" style={{ fontSize: "inherit", fontFamily: "inherit" }}
              data-testid={`field-${fieldKey}`} />
          ) : (
            <EditableCombobox field={optKey || fieldKey} value={value} onChange={onChange}
              options={opt(optKey || fieldKey)} onAdd={addOption} onDelete={deleteOption} placeholder="" inlineEdit={inline} testid={`field-${fieldKey}`} />
          )
        ) : (
          <span className="whitespace-pre-wrap">{isDate ? fmtDate(value) : value}</span>
        )}
      </div>
    );
  };

  return (
    <div className={`paper preprint-layer relative overflow-hidden ${transparent ? "" : "bg-white"}`}
      style={{ width: "210mm", minHeight: "297mm", boxShadow: transparent ? "none" : "0 20px 60px -15px rgba(0,0,0,0.18)" }}
      data-testid="preprint-layer">
      {templateSrc && (
        <img src={templateSrc} alt="paper" className={`block w-full h-auto pointer-events-none ${printTemplate ? "" : "no-print"}`} style={{ opacity: printTemplate ? 1 : 0.4 }} />
      )}
      {showGuides && (
        <div className="absolute inset-0 no-print pointer-events-none">
          {/* mm grid every 10mm for visual calibration */}
          <div style={{
            position: "absolute", inset: 0,
            backgroundImage:
              "repeating-linear-gradient(to right, rgba(2,132,199,0.08) 0, rgba(2,132,199,0.08) 1px, transparent 1px, transparent 10mm)," +
              "repeating-linear-gradient(to bottom, rgba(2,132,199,0.08) 0, rgba(2,132,199,0.08) 1px, transparent 1px, transparent 10mm)",
          }} />
        </div>
      )}

      {renderField({ fieldKey: "patient_name", value: data.patient_name, onChange: (v) => setField("patient_name", v) })}
      {renderField({ fieldKey: "date", value: data.date, onChange: (v) => setField("date", v), isDate: true })}
      {renderField({ fieldKey: "age_gender", value: data.age_gender, onChange: (v) => setField("age_gender", v), inline: true })}
      {renderField({ fieldKey: "drug_allergy", value: data.drug_allergy, onChange: (v) => setField("drug_allergy", v) })}
      <NotesBlock
        value={data.notes}
        onChange={(v) => setField("notes", v)}
        options={opt("notes")}
        addOption={addOption}
        deleteOption={deleteOption}
        editable={editable}
        fontSizePt={f.notes.fontSize}
        style={{
          position: "absolute",
          top: `${oy + NOTES.top - comp}mm`,
          left: `${ox + NOTES.left}mm`,
          width: `${NOTES.width}mm`,
          ...(editable ? {} : { height: `${NOTES.height}mm` }),
        }}
      />

      {/* Medicine rows — each cell is vertically centred on the printed row */}
      {data.medicines.map((m, i) => {
        const cy = oy + MED_ROW_CENTER0 + i * MED_ROW_STEP - comp;
        return TABLE_COLS.map((c) => {
          const base = {
            position: "absolute",
            top: `${cy}mm`,
            left: `${ox + colLeft[c]}mm`,
            width: `${t.cols[c]}mm`,
            fontSize: `${t.fontSize}pt`,
            transform: "translateY(-50%)",
          };
          if (c === "s_no") {
            return (
              <div key={`${i}-${c}`} style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }} className="font-paper leading-none" data-testid={`pp-med-${i}-s_no`}>
                {m.name ? i + 1 : ""}
              </div>
            );
          }
          if (c === "name") {
            if (editable) {
              return (
                <div key={`${i}-${c}`} style={{ ...base, paddingLeft: "1.5mm", display: "flex", alignItems: "center" }} className="font-paper leading-tight">
                  <MedicineSearch value={m.name} salt={m.salt} catalog={medCatalog}
                    onSelect={(med) => applyMed(i, med)} onAdd={onAddMedicine} onClear={() => clearMed(i)}
                    fontSize={t.fontSize} testid={`med-${i}-name`} />
                </div>
              );
            }
            return (
              <div key={`${i}-${c}`} style={{ ...base, height: `${MED_ROW_STEP}mm`, paddingLeft: "1.5mm", paddingRight: "1mm", overflow: "hidden" }} className="font-paper">
                <MedNameCell name={m.name} salt={m.salt} nameFontPt={t.fontSize} />
              </div>
            );
          }
          if (c === "total_qty") {
            return (
              <div key={`${i}-${c}`} style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }} className="font-paper leading-none" data-testid={`pp-med-${i}-total`}>
                {computeMedTotal(m)}
              </div>
            );
          }
          return (
            <div key={`${i}-${c}`} style={{ ...base, display: "flex", alignItems: "center", justifyContent: "center" }} className="font-paper leading-none">
              {editable ? (
                <EditableCombobox field={c} value={m[c]} onChange={(v) => setMed(i, c, v)}
                  options={opt(c)} onAdd={addOption} onDelete={deleteOption}
                  bare center align="center" testid={`med-${i}-${c}`} />
              ) : (
                <span>{m[c]}</span>
              )}
            </div>
          );
        });
      })}

      {/* Doctor signature (transparent PNG) placed above the pre-printed signature line */}
      {signature ? (
        <img src={signature} alt="signature" data-testid="pp-signature"
          className="pointer-events-none"
          style={{
            position: "absolute",
            top: `${oy + SIGNATURE.top - comp}mm`,
            left: `${ox + SIGNATURE.left}mm`,
            width: `${SIGNATURE.width}mm`,
            height: `${SIGNATURE.height}mm`,
            objectFit: "contain",
            objectPosition: "left bottom",
          }} />
      ) : null}

      {/* Doctor picker — screen only, never printed */}
      {editable && (
        <div className="no-print" data-testid="doctor-pick"
          style={{ position: "absolute", top: `${oy + DOCTOR_PICK.top}mm`, left: `${ox + DOCTOR_PICK.left}mm`, width: `${DOCTOR_PICK.width}mm` }}>
          <Select value={doctor || undefined} onValueChange={onDoctorChange}>
            <SelectTrigger className="h-7 text-xs bg-white/90 border-sky-300" data-testid="doctor-select">
              <SelectValue placeholder="Select doctor" />
            </SelectTrigger>
            <SelectContent className="bg-white z-50">
              {doctorList.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No doctors</div>}
              {doctorList.map((d) => (<SelectItem key={d.id} value={d.name} data-testid={`doctor-option-${d.id}`}>{d.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
